import styles from 'components/cases/common/Interview/Interview.module.scss'
import TitleFlex from 'components/cases/common/TitleFlex'
import { useEffect, useRef } from 'react'
import { gsap, Power4 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import InterviewContents from 'components/cases/common/Interview/InterviewContents'
import { ContentData } from 'components/cases/common/Interview/fragments/type'
gsap.registerPlugin(ScrollTrigger)

const sectionTitle: { title: string; sub_title: string } = {
  title: 'Interview',
  sub_title: 'インタビュー',
}

const contentData: ContentData[][] = [
  [
    {
      type: 'sectionTitle',
      text: 'グループをつなぐためのアプリ開発へ',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/heart-one/interview01.webp',
      alt: '左から大和ハウスフィナンシャル株式会社の小塩さん、氏川さん、岡田さん、アイスリーデザインの今野、田澤',
      text: '左から大和ハウスフィナンシャル株式会社の小塩さん、氏川さん、岡田さん、アイスリーデザインの今野、田澤',
    },
    {
      type: 'textBox',
      texts: ['―― 「HeartOneポータルアプリ」開発の経緯を教えてください。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '岡田さん',
      texts: [
        <>
          <p>
            大和ハウスグループは、住宅や商業施設から金融サービスまで多岐にわたる事業を展開しています。
            <br />
            そのぶん、お客様との接点も非常に幅広い。一方で、LINEやメールなどチャネルごとに情報発信が分散し、「本当に届けたいユーザーに適切な情報が届いているのか？」という課題感がありました。
          </p>
          <p>
            <span className={styles.space_top}>
              そこで、
              <span className={styles.textbold}>
                グループ全体のサービスをまとめて横断し、お客様をサポートする仕組みが必要
              </span>
              と考え、最初のステップとして『HeartOneポータルアプリ』を立ち上げることにしました。
            </span>
          </p>
        </>,
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: 'パートナー選定の決め手。“何のためのアプリか”から伴走してくれた',
    },
    {
      type: 'textBox',
      texts: ['―― 複数のベンダーを検討されたと思うのですが、アイスリーデザインを選ばれたきっかけは？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '氏川さん',
      texts: [
        <>
          <p>
            弊社にはアプリ開発の知見があまりなく、何から始めればいいか悩んでいました。以前、別プロジェクトでアイスリーデザインさんにスマホ最適化をお願いしたとき、
            <span className={styles.textbold}>想定外の課題にも丁寧に対応してくれた印象が強くて。</span>
            <br />
            今回も「アプリ開発をリードしてほしい」という想いから、複数社の提案を比較したうえで、最終的にアイスリーデザインさんを選びました。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '今野',
      texts: [
        <>
          <p>
            多くのクライアント様が「あれもこれも実装したい」という思いをお持ちです。ただし開発コストやスケジュールの兼ね合いもあるので、本当に必要なコア機能を見極めることが大切です。最初は大和ハウスフィナンシャル様のアイデアをすべてヒアリングし、そのうえで
            <span className={styles.textbold}>優先度を整理して提案させていただきました。</span>
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/heart-one/interview02.webp',
      alt: 'インタビュー写真',
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: 'プロジェクト進行とデザインへのこだわり',
    },
    {
      type: 'textBox',
      texts: ['―― プロジェクトの進行や開発過程で印象に残っていることはありますか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '小塩さん',
      texts: [
        <>
          <p>
            要望が多岐にわたっていたので、正直どう整理したらいいのか手探りでした。アイスリーデザインさんに
            <span className={styles.textbold}>
              プロダクトマネジメント面をサポートしていただいたおかげで、リリースに必須な機能と、今後検討する機能をスムーズに切り分けられたんです。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              そして個人的に一番大変だったのは、AppleやGoogleへの申請ですね。年末年始や海外との時差の影響で、申請しても返信がなかなか来ないことがあり、営業時間外にもかかわらず、アイスリーデザインさんから『もう一度連絡してみます』とメールが届いていたり…。お忙しい中、迅速に対応していただき、本当に感謝しております。おかげでスムーズにリリースできそうな見通しが立ちました。
            </span>
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '田澤',
      texts: [
        <>
          <p>
            海外のAppleやGoogleとの申請業務は想定外のやりとりも多かったのですが、チームで連携して迅速に対処し、スムーズに進行することができました。
            <br />
            あとは
            <span className={styles.textbold}>
              クレジットカード情報などセンシティブなデータを扱うので、セキュリティを最優先に設計
            </span>
            しました。通信の暗号化や権限管理の仕組みを整えつつ、ユーザーの操作が複雑になりすぎないよう意識しています。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/heart-one/interview03.webp',
      alt: 'HeartOneポータルアプリの画面',
    },
    {
      type: 'greyBox',
      name: '岡田さん',
      texts: [
        <>
          <p>
            キャラクターの使い方などで社内からいろいろ意見はありましたが、最終的にはユーザーが迷わず使えるUIに仕上げてもらえたと思います。アイスリーデザインさんからも『セキュリティを担保しつつ、迷わず操作できるUIが大事』と言われ、
            <span className={styles.textbold}>具体的な根拠を挙げながら複数のデザイン案を提示してくれた</span>
            ので、社内の合意形成もしやすかったですね。
            <br />
            他にもカード情報の管理を扱うアプリなので、安心感を損なわないよう細かな点までご提案いただけました。
            <br />
            結果的に、キャラクターでポップさを出しつつも、クレジットカードの管理などセンシティブな機能はしっかり安心感が持てるデザインに仕上がったと思います。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/heart-one/interview04.webp',
      alt: '改善したUX/UIデザイン',
    },
    {
      type: 'greyBox',
      name: '今野',
      texts: [
        <>
          <p>
            デザインに取り組む前に、ユーザーインタビューを行いました。6名にインタビューを行い、3通りのペルソナを作成。アプリにおける方針を決めた上でデザインを行いました。ユーザーがアプリで優先的に確認したい情報は大きく2つで、クレジットカードの支払額と、各ショッピングモールのポイントカードということがわかりました。これらを踏まえてデザインに反映させました。
          </p>
          <p>
            <span className={styles.space_top}>今回のデザインの特徴は、ホーム画面のみで完結するということです。</span>
          </p>
          <p>
            <span className={styles.space_top}>
              直感的に操作できるUIにするため、クレジットカードの支払額とショッピングモールのポイントカードをタブで行き来できるようにする工夫をしました。他にも、クレジットカードの金額表示を当月の確定分と、来月以降の未確定分が混乱しないように文字サイズ・テキストの色・タグを付けることで、一度見たら迷わないような表示にしています。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              <span className={styles.textbold}>
                金額に関することなので誤認識が起こらないような配慮をしたことが、ユーザー様の安心感に繋がっている
              </span>
              のではないかと思います。
            </span>
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/heart-one/interview05.webp',
      alt: '各ペルソナの傾向 インタビューの概要',
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: 'アプリを育てグループ全体のエコシステム強化へ',
    },
    {
      type: 'textBox',
      texts: ['―― 最後に「HeartOneポータルアプリ」の今後の展望を教えてください。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '小塩さん',
      texts: [
        <>
          <p>
            このアプリを軸に、他のグループ会社や外部サービスとも連携することで、“グループ全体のお客様をつなぐ”仕組みを実現したいと考えています。アイスリーデザインさんとは、リリース後も運用や機能拡張で伴走していただきながら、アプリをより良いものに育てていきたいですね。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/heart-one/interview06.webp',
      alt: 'インタビュー写真',
    },
    {
      type: 'greyBox',
      name: '今野',
      texts: [
        <>
          <p>
            リリースはゴールではなくスタートだと考えています。実際に使われる中で得られるフィードバックをいかに素早く反映し、より良い体験へつなげていくかが重要です。これからもユーザー様の利便性を高める施策を弊社からも提案させていただき、多くの方に継続的に利用されるアプリに育てていくご支援ができればと思います。大和ハウスグループ様の中長期的な戦略実現に向けて引き続き伴走いたします。
          </p>
        </>,
      ],
    },
  ],
]

const tweenVars: gsap.TimelineVars = {
  y: 50,
  duration: 1,
  opacity: 0,
  ease: Power4.easeOut,
}

const Interview: React.FC = () => {
  const timeline = gsap.timeline()
  const wrapper = useRef<HTMLElement>()
  const image = useRef<HTMLImageElement>()
  const text_container = useRef<HTMLDivElement>()

  useEffect(() => {
    timeline.from(image.current, tweenVars, 0.2)
    timeline.from(text_container.current, tweenVars, 0.4)

    ScrollTrigger.create({
      animation: timeline,
      trigger: wrapper.current,
      start: 'top-=100 90%',
      toggleActions: 'play none none play',
    })
  }, [])

  return (
    <section className={styles.wrapper} ref={wrapper}>
      <div className={styles.content}>
        <TitleFlex sectionTitle={sectionTitle} />
        <div className={styles.article_container} ref={text_container}>
          <InterviewContents contentData={contentData} />
        </div>
      </div>
    </section>
  )
}

export default Interview
